import React, { useMemo } from "react";
import { cn } from "@/lib/utils";
import { SessionConnectionStatus } from "@/services/interfaces/Session";

interface TimerProps {
  timer: number;
  className?: string;
  isVisible?: boolean;
  sessionConnectionStatus?: SessionConnectionStatus;
}

// Utility function to pad a number with leading zeros
const padWithLeadingZeros = (num: number, length: number): string => {
  return String(num).padStart(length, "0");
};

export const Timer: React.FC<TimerProps> = ({
  timer,
  className,
  isVisible = true,
  sessionConnectionStatus,
}) => {
  // Calculate the hours, minutes, and seconds from the timer
  const hours = Math.floor(timer / 3600);
  const minutes = Math.floor((timer % 3600) / 60);
  const seconds = timer % 60;

  // Split the hours, minutes, and seconds into individual digits
  const [hourLeft, hourRight] = useMemo(
    () => padWithLeadingZeros(hours, 2).split(""),
    [hours]
  );
  const [minuteLeft, minuteRight] = useMemo(
    () => padWithLeadingZeros(minutes, 2).split(""),
    [minutes]
  );
  const [secondLeft, secondRight] = useMemo(
    () => padWithLeadingZeros(seconds, 2).split(""),
    [seconds]
  );

  // 获取状态圆点的样式
  const getStatusDotStyle = () => {
    switch (sessionConnectionStatus) {
      case SessionConnectionStatus.Connected:
        return "bg-green-500 animate-pulse";
      case SessionConnectionStatus.Connecting:
        return "bg-yellow-500 animate-pulse";
      case SessionConnectionStatus.Error:
        return "bg-red-500";
      case SessionConnectionStatus.Disconnected:
      default:
        return "bg-gray-400";
    }
  };

  // 获取状态文字
  const getStatusText = () => {
    switch (sessionConnectionStatus) {
      case SessionConnectionStatus.Connected:
        return "录制中";
      case SessionConnectionStatus.Connecting:
        return "连接中";
      case SessionConnectionStatus.Error:
        return "错误";
      case SessionConnectionStatus.Disconnected:
      default:
        return "已断开";
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        "items-center gap-1 font-mono font-medium text-foreground flex text-xs",
        // 🔑 移动端去掉边框和背景，桌面端保持原样
        "border-0 p-0 bg-transparent shadow-none sm:border sm:p-1 sm:rounded-md sm:bg-card sm:shadow-sm",
        className
      )}
    >
      <div className="flex items-center gap-0.5">
        <span className="rounded-sm bg-background px-0.5 py-0 sm:px-1 sm:py-0.5 text-foreground text-[10px] sm:text-xs">
          {hourLeft}
        </span>
        <span className="rounded-sm bg-background px-0.5 py-0 sm:px-1 sm:py-0.5 text-foreground text-[10px] sm:text-xs">
          {hourRight}
        </span>
        <span className="text-[10px] sm:text-xs">:</span>
        <span className="rounded-sm bg-background px-0.5 py-0 sm:px-1 sm:py-0.5 text-foreground text-[10px] sm:text-xs">
          {minuteLeft}
        </span>
        <span className="rounded-sm bg-background px-0.5 py-0 sm:px-1 sm:py-0.5 text-foreground text-[10px] sm:text-xs">
          {minuteRight}
        </span>
        <span className="text-[10px] sm:text-xs">:</span>
        <span className="rounded-sm bg-background px-0.5 py-0 sm:px-1 sm:py-0.5 text-foreground text-[10px] sm:text-xs">
          {secondLeft}
        </span>
        <span className="rounded-sm bg-background px-0.5 py-0 sm:px-1 sm:py-0.5 text-foreground text-[10px] sm:text-xs">
          {secondRight}
        </span>
      </div>
      {sessionConnectionStatus && (
        <div className="flex items-center gap-0.5 sm:gap-1 ml-0.5 sm:ml-1">
          <div
            className={cn(
              "w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full",
              getStatusDotStyle()
            )}
          />
          <span className="text-[10px] sm:text-xs text-muted-foreground">
            {getStatusText()}
          </span>
        </div>
      )}
    </div>
  );
}; 